import React, { useEffect, useState } from 'react';
import { Button, Spin, message } from 'antd';
import dayjs from 'dayjs';
import s from './s.module.less';
import AIChat from 'components/AIChat';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import ProfileCard from './components/ProfileCard';
import ServicesCard from './components/ServicesCard';
import PersonalIndentification from './components/PersonalIndentification';
import MedicalLicenses from './components/MedicalLicenses';
import PaymentInformation from './components/PaymentInformation';
import WorkingHours from './components/WorkingHours';
import DateTimeOverride from './components/DateTimeOverride';
import MinimumTimeAdvance from './components/MinimumTimeAdvance';
import ConsentForms from './components/ConsentForms';
import WarningTips from 'components/WarningTips';
import useLoadBasicData from 'hooks/useLoadBasicData';
import usePPAOnboardingReviewInfo from 'hooks/usePPAOnboardingReviewInfo';
import useGetPPAServiceDetail from 'hooks/useGetPPAServiceDetail';
import { submitReview, saveStep } from 'api/ppaOnboarding';
import { EPPAStep } from 'types/ppaOnboarding';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { isMobile } from 'utils/common';
import { trackHeapEvent } from 'utils/analytics';
import { EAIChatPageId } from 'types/ai';

export default function PPAOnboardingReviewPage() {
    const [basicAPICalling] = useLoadBasicData();
    const [reviewData, loading, loadReviewData] = usePPAOnboardingReviewInfo();
    const [serviceDetail] = useGetPPAServiceDetail();
    const navigate = useNavigate();
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        basicAPICalling();
    }, [basicAPICalling]);

    const handleSubmit = async () => {
        try {
            setSubmitLoading(true);
            trackHeapEvent('WebApp - PPA Onboarding Review - Click - Submit for Review', {
                hasDisqualifyReason: !!reviewData.disqualifyReason,
            });

            // 验证ID和医疗执照是否已上传
            const isIdentificationMissing = !reviewData.identification?.frontUrl;
            const isLicenseMissing =
                !reviewData.licenses?.length || !reviewData.licenses?.some((license) => license.fullUrl);

            if (isIdentificationMissing && isLicenseMissing) {
                message.error(
                    'Government-issued ID and medical license are required. Please upload the documents to submit.',
                );
                setSubmitLoading(false);
                return;
            }

            if (isIdentificationMissing) {
                message.error('Government-issued ID is required. Please upload the document to submit.');
                setSubmitLoading(false);
                return;
            }

            if (isLicenseMissing) {
                message.error('Medical license is required. Please upload the document to submit.');
                setSubmitLoading(false);
                return;
            }

            // 并行执行保存步骤和提交审核请求
            const [saveStepRes, submitRes] = await Promise.all([
                saveStep(EPPAStep.STEP_18),
                submitReview({
                    submitTime: dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
                }),
            ]);

            // 检查保存步骤是否成功
            if (saveStepRes.error) {
                trackHeapEvent('WebApp - PPA Onboarding Review - View - Save Step Failed', {
                    error: saveStepRes.error,
                });
                // eslint-disable-next-line no-console
                console.error('Failed to save step:', saveStepRes.error);
                return;
            }

            // 检查提交审核是否成功
            if (!submitRes.error) {
                trackHeapEvent('WebApp - PPA Onboarding Review - View - Submit Success', {
                    hasDisqualifyReason: !!reviewData.disqualifyReason,
                });
                navigate(`${PATH.PPA_ONBOARDING_FINISH}?from=onboarding`);
            } else {
                trackHeapEvent('WebApp - PPA Onboarding Review - View - Submit Failed', {
                    error: submitRes.error,
                });
            }
        } catch (error) {
            trackHeapEvent('WebApp - PPA Onboarding Review - View - Submit Error', {
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleBack = async (mobile: boolean) => {
        trackHeapEvent('WebApp - PPA Onboarding Review - Click - Back', {
            isMobile: mobile,
        });
        // 返回上一步（Consent Forms，STEP_17）。需要把已保存的步骤重置回 STEP_17，
        // 否则 Steps 页面读取到 STEP_17_1 会立即重定向回 Review。
        try {
            await saveStep(EPPAStep.STEP_17);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to reset step before back:', error);
        }
        navigate(PATH.PPA_ONBOARDING_STEPS);
    };

    const isLicenseWarning = reviewData.disqualifyReason === 'Medical license expired / invalid';
    const isIdentificationWarning = reviewData.disqualifyReason === 'Government-issued ID expired / invalid';

    return (
        <Spin spinning={loading}>
            <div className={s.wrap}>
                <PPAOnboardingHeader />
                <div className={s.content}>
                    <div className={s.contentWrap}>
                        <div className={s.contentIn}>
                            <div className={s.title}>Review your setup before going live</div>
                            <div className={s.desc}>
                                Please confirm your information before moving forward. You can always edit later if
                                something changes.
                            </div>
                            {reviewData.disqualifyReason && (
                                <div className="mb-6">
                                    <WarningTips title="Some sections need your attention before you can go live. Please review the highlighted areas below." />
                                </div>
                            )}
                            <div className="flex flex-col mb-6">
                                <ProfileCard initValue={reviewData.basicInfo} onSave={loadReviewData} />
                                <ServicesCard
                                    initValue={reviewData.services}
                                    specialty={serviceDetail?.selectedSpecialty}
                                    onSave={loadReviewData}
                                />
                                <PersonalIndentification
                                    initValue={reviewData.identification}
                                    onSave={loadReviewData}
                                    isWarning={isIdentificationWarning}
                                />
                                <MedicalLicenses
                                    initValue={reviewData.licenses}
                                    onSave={loadReviewData}
                                    isWarning={isLicenseWarning}
                                />
                                <PaymentInformation initValue={reviewData.paymentInfo} onSave={loadReviewData} />
                                <WorkingHours initValue={reviewData.availability} onSave={loadReviewData} />
                                <DateTimeOverride initValue={reviewData.override} onSave={loadReviewData} />
                                <MinimumTimeAdvance initValue={reviewData.calendarSetting} onSave={loadReviewData} />
                                <ConsentForms onSave={loadReviewData} />
                            </div>
                            {!isMobile() && (
                                <div className="flex gap-4 mb-9">
                                    <Button style={{ background: '#fff' }} onClick={() => handleBack(false)}>
                                        Back
                                    </Button>
                                    <Button type="primary" onClick={handleSubmit} loading={submitLoading}>
                                        Submit for review
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className={s.chatWrap}>
                            <AIChat pageId={EAIChatPageId.PROVIDER_ONBOARDING_REVIEW} />
                        </div>
                    </div>
                </div>
                {isMobile() && (
                    <div className={s.mobileBtnWrap}>
                        <Button onClick={() => handleBack(true)} className={s.mobileBtn} style={{ background: '#fff' }}>
                            Back
                        </Button>
                        <Button type="primary" onClick={handleSubmit} loading={submitLoading} className={s.mobileBtn}>
                            Submit for review
                        </Button>
                    </div>
                )}
            </div>
        </Spin>
    );
}
